import { useQueries, useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";

export type AccountCooldownState = { connect: boolean; inmail: boolean };

async function getCooldownState(
  workspaceId: string,
  accountId: string,
): Promise<AccountCooldownState> {
  const { cooldown } = await get(
    `workspaces/${workspaceId}/accounts/${accountId}/cooldown`,
  );
  return cooldown;
}

const cooldownQueryOptions = (workspaceId: string, accountId: string) => ({
  queryKey: getQueryKeys(workspaceId).accountsKeys.cooldown(accountId),
  queryFn: () => getCooldownState(workspaceId, accountId),
  refetchOnMount: false,
});

export default function useCooldownState(accountId: string) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { data: cooldownState, isLoading: isLoadingCooldownState } = useQuery({
    ...cooldownQueryOptions(workspaceId, accountId),
  });

  return {
    cooldownState,
    isLoadingCooldownState,
  };
}

export function useCooldownStatesForAccounts(accountIds: string[]) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  // useQueries hook allows us to run multiple queries in parallel
  const queries = useQueries({
    queries: accountIds.map((accountId) => ({
      ...cooldownQueryOptions(workspaceId, accountId),
    })),
  });

  const cooldownStates: Record<string, AccountCooldownState | undefined> = {};

  for (let index = 0; index < accountIds.length; index++) {
    const accountId = accountIds[index];
    const query = queries[index];

    if (!query || query.isLoading || !query.data) {
      cooldownStates[accountId] = undefined;
    } else {
      cooldownStates[accountId] = query.data;
    }
  }

  return { cooldownStates };
}
